import { DatePipe, DecimalPipe } from '@angular/common'
import { Component, EventEmitter, Output } from '@angular/core'
import { RouterLink } from '@angular/router'

import { BaseComponent, FeatureType } from '../../shared'

interface VehicleInspectionDashboardKpi {
    inspectionsThisWeek: number
    inspectionsThisMonth: number
    vehiclesInspectedThisMonth: number
    vehiclesOverdueThisMonth: number
    totalInspectableVehicles: number
}

interface PeriodCount {
    label: string
    count: number
}

interface CoverageRow {
    equipmentCode: string
    description: string
    mainCategory: string
    lastInspectionDate: string | null
    daysSinceLastInspection: number | null
    lastHourReading: number | null
    currentHourReading: number | null
    hoursSinceLastInspection: number | null
    dueRule: 'Days' | 'Hours' | string
    focusInspectionId: string | null
}

interface Coverage {
    overdue: CoverageRow[]
    completed: CoverageRow[]
}

interface OpenNcByVehicleRow {
    equipmentCode: string
    vehicleDescription: string
    openItems: number
    focusInspectionId: string | null
}

interface ConexTrailerSettingRow {
    equipmentCode: string
    description: string
    mainCategory: string
    isInspectionActive: boolean
}

interface VehicleInspectionDashboardMetrics {
    kpi: VehicleInspectionDashboardKpi
    weeklyCounts: PeriodCount[]
    monthlyCounts: PeriodCount[]
    coverage: Coverage
    openNcByVehicle: OpenNcByVehicleRow[]
    conexTrailerSettings: ConexTrailerSettingRow[]
}

const emptyMetrics = (): VehicleInspectionDashboardMetrics => ({
    kpi: {
        inspectionsThisWeek: 0,
        inspectionsThisMonth: 0,
        vehiclesInspectedThisMonth: 0,
        vehiclesOverdueThisMonth: 0,
        totalInspectableVehicles: 0,
    },
    weeklyCounts: [],
    monthlyCounts: [],
    coverage: { overdue: [], completed: [] },
    openNcByVehicle: [],
    conexTrailerSettings: [],
})

@Component({
    selector: 'app-vehicle-inspection-dashboard',
    templateUrl: './vehicle-inspection-dashboard.component.html',
    styleUrls: ['./vehicle-inspection-dashboard.component.css'],
    standalone: true,
    imports: [DatePipe, DecimalPipe, RouterLink],
})
export class VehicleInspectionDashboardComponent extends BaseComponent {
    @Output() ready = new EventEmitter<void>()
    @Output() addForEquipment = new EventEmitter<{ equipmentCode: string; description: string }>()

    metrics: VehicleInspectionDashboardMetrics = emptyMetrics()
    metricsLoadError = false
    togglingEquipmentCode: string | null = null
    private hasEmittedReady = false

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'vehicleInspectionDashboard',
            true,
            this.services.data
                .get<VehicleInspectionDashboardMetrics>('/api/VehicleInspection/GetDashboardMetrics')
                .subscribe({
                    next: result => {
                        this.metrics = result ?? emptyMetrics()
                        if (!this.metrics.conexTrailerSettings) {
                            this.metrics.conexTrailerSettings = []
                        }
                        this.metricsLoadError = false
                        this.emitReady()
                    },
                    error: () => {
                        this.metrics = emptyMetrics()
                        this.metricsLoadError = true
                        this.emitReady()
                    },
                }),
        )
    }

    toggleInspectionActive(row: ConexTrailerSettingRow) {
        if (!this.canEditInspections || this.togglingEquipmentCode) return

        const nextActive = !row.isInspectionActive
        this.togglingEquipmentCode = row.equipmentCode
        this.services.data
            .put<ConexTrailerSettingRow>('/api/VehicleInspection/SetInspectionActive', {
                equipmentCode: row.equipmentCode,
                isInspectionActive: nextActive,
            })
            .subscribe({
                next: updated => {
                    row.isInspectionActive = updated?.isInspectionActive ?? nextActive
                    this.togglingEquipmentCode = null
                    this.refresh()
                },
                error: () => {
                    this.togglingEquipmentCode = null
                    this.services.notification.error('Could not update inspection-active setting.')
                },
            })
    }

    inspectionListQueryParams(focusInspectionId: string | null): Record<string, string> | null {
        if (focusInspectionId) {
            return { openInspectionId: focusInspectionId }
        }
        return null
    }

    get canViewInspectionList() {
        return this.services.authentication.hasFeature([FeatureType.SafetyVehicleInspectionView])
    }

    get canEditInspections() {
        return this.services.authentication.hasFeature([FeatureType.SafetyVehicleInspectionEdit])
    }

    isHourlyRow(row: CoverageRow): boolean {
        return row.dueRule === 'Hours'
    }

    formatEquipmentDisplay(equipmentCode: string, description: string): string {
        if (!description || description === equipmentCode) {
            return equipmentCode
        }
        return `${equipmentCode} — ${description}`
    }

    private emitReady() {
        this.initialLoadingComplete()
        if (!this.hasEmittedReady) {
            this.hasEmittedReady = true
            this.ready.emit()
        }
    }

    maxWeeklyCount(): number {
        return Math.max(...this.metrics.weeklyCounts.map(w => w.count), 1)
    }

    maxMonthlyCount(): number {
        return Math.max(...this.metrics.monthlyCounts.map(m => m.count), 1)
    }
}
